import React, { useState, useRef } from 'react';
import { Button, Chip, CircularProgress } from 'drift-ui';
import Toggle from '../../core/toggle';
import styles from '../styles/ptPlanDialog.scss';
import PtSubscriptionPlanSection from './PtSubscriptionPlanSection';
import PtEnterPayment from './ptEnterPayment';
import { useGetPtSubscriptionPricingQuery } from '../../../state/paymentService';
import YearlyDiscount from './yearlyDiscount';
import { useTranslation } from 'react-i18next';
import ContactForm from './ContactForm';

const PtPlanDialog: React.FC = () => {
  const [isMonthly, setIsMonthly] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const { data: ptSubscriptionPricing, isLoading } = useGetPtSubscriptionPricingQuery(undefined);
  const { t } = useTranslation('common', { keyPrefix: 'proTransportSubscriptions' });

  const premiumStatus = isMonthly ? (
    <div>
      <span className={styles.priceAmount}>{`$${ptSubscriptionPricing?.premium?.monthly.toFixed(2)}`}</span>
      <span className={styles.pricePeriod}>/{t('month')}</span>
    </div>
  ) : (
    <div>
      <span className={styles.priceAmount}>{`$${ptSubscriptionPricing?.premium?.yearly.toFixed(2)}`}</span>
      <span className={styles.pricePeriod}>/{t('year')}</span>
    </div>
  );

  const handleUpgradeToPremium = () => {
    setShowPaymentForm(true);
    setShowContactForm(false);
  };

  const handleBackToPlanSelection = () => {
    setShowPaymentForm(false);
  };

  if (showPaymentForm) {
    return <PtEnterPayment isMonthly={isMonthly} setIsMonthly={setIsMonthly} onBack={handleBackToPlanSelection} />;
  }

  return (
    <>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress size={80} />
        </div>
      )}
      {!isLoading && ptSubscriptionPricing && (
        <div className={styles.ptPlanDialog}>
          <div>
            <div className={styles.titleContainer}>
              <div className={styles.selectPlan}>{t('select_plan')}</div>
              <Toggle
                leftText={t('Monthly')}
                rightText={t('Yearly')}
                onChange={(index) => setIsMonthly(index === 0)}
                selectedIndex={isMonthly ? 0 : 1}
              />
              <YearlyDiscount amount={15} />
            </div>
            <div className={styles.subscriptionBlurbContainer}>
              <PtSubscriptionPlanSection
                title={t('protransport_basic')}
                status={t('included_with_RTSPro')}
                includesTitle={t('included_with_basic')}
                includesText={[
                  t('mobile_and_web_access'),
                  t('basic_load_entry'),
                  t('ITFA'),
                  t('unlimited_users'),
                  t('unlimited_trucks_drivesrs_loads'),
                  t('loads_on_credit'),
                  t('manage_booked_loads'),
                  t('direct_invoicing'),
                ]}
                button={<Chip color="primary" variant="outlined" label={t('current_plan')} />}
              />
              <PtSubscriptionPlanSection
                title={t('protransport_premium')}
                status={premiumStatus}
                statusInfo={t('billed_by_period', {
                  period: isMonthly ? t('monthly') : t('yearly'),
                })}
                includesTitle={t('included_with_premium')}
                includesSubtitle={t('everything_in_basic_plus')}
                includesText={[
                  t('advanced_load_entry'),
                  t('expanded_My_Fleet_profiles'),
                  t('wex_fuel_import'),
                  t('driver_pay_statements'),
                  t('email_invoices'),
                  t('aging_reporting'),
                  t('dispatch_board'),
                ]}
                button={
                  <Button
                    className={styles.subscriptionButtons}
                    color="primary"
                    variant="contained"
                    onClick={handleUpgradeToPremium}
                  >
                    {t('upgrade_to_premium')}
                  </Button>
                }
              />
              <PtSubscriptionPlanSection
                title={t('protransport_enterprise')}
                status={t('coming_this_fall')}
                statusInfo={t('power_of_ProTransport')}
                includesTitle={t('included_with_enterprise')}
                includesSubtitle={t('everything_in_premium_plus')}
                includesText={[
                  t('multiple_integrations'),
                  t('accounting_safety_maintenance'),
                  t('dedicated_implementation'),
                  t('driver_mobile_app'),
                ]}
                button={
                  <Button
                    className={styles.subscriptionButtons}
                    color="primary"
                    variant="contained"
                    onClick={() => setShowContactForm(true)}
                  >
                    {t('contact_you')}
                  </Button>
                }
                extraInfoTitle={t('want_to_know_more')}
                extraInfoText={t('representative_contact_you')}
              />
            </div>
          </div>
          {showContactForm && <ContactForm />}
        </div>
      )}
    </>
  );
};

export default PtPlanDialog;
